import { Label } from '@/lib/base/forms/input/label';

export interface LabelWithIndicatorProps {
  /** The label text to display */
  label: string;
  /** Whether the field is required (shows asterisk) */
  isRequired?: boolean;
  /** Description to show in tooltip */
  description?: string;
  /** Whether the field has been edited (shows blue dot) */
  isEdited?: boolean;
}

/**
 * LabelWithIndicator - CRM-specific label wrapper that adds edit indicators.
 *
 * This component composes the base Label component and adds:
 * - Blue dot indicator when isEdited=true (CRM-specific feature)
 *
 * The label is rendered exactly as provided — shipped labels are already
 * title case, and customer-defined labels must not be recased.
 *
 * Base Label provides:
 * - Required asterisk when isRequired=true
 * - Tooltip with HelpCircle icon when description provided
 *
 * @example
 * <LabelWithIndicator
 *   label="Email Address"
 *   isRequired
 *   description="The contact's primary email"
 *   isEdited
 * />
 */
export function LabelWithIndicator({
  label,
  isRequired = false,
  description,
  isEdited = false,
}: LabelWithIndicatorProps) {
  return (
    <span className='flex items-center gap-1.5 w-full'>
      {isEdited && (
        <span className='w-2 h-2 bg-blue-500 rounded-full flex-shrink-0' />
      )}
      <Label
        isRequired={isRequired}
        tooltip={description}
        className='flex-1 !text-primary !font-semibold'
      >
        {label}
      </Label>
    </span>
  );
}
